import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { getTranslate } from 'legacy-src/utilities/i18n'
import Markdown from 'markdown-to-jsx'
import Link from 'next/link'
import React, { memo, useState } from 'react'

import SpeaksLanguages from '@/legacy-src/components/SpeaksLanguages'
import TherapistCategory from '@/legacy-src/components/TherapistCategory'
import { ButtonLink } from '@/legacy-src/components/ui/button'
import YoutubeVideo from '@/legacy-src/components/YoutubeVideo'
import useCompanyBenefits from '@/legacy-src/hooks/useCompanyBenefits'
import useServices from '@/legacy-src/hooks/useServices'
import useTherapistCategories from '@/legacy-src/hooks/useTherapistCategories'
import useUser from '@/legacy-src/hooks/useUser'
import { cn } from '@/legacy-src/lib/utils'

import { Tag } from '@/src/domains/design-system/components/Tag'
import { TherapistCardEventType } from '@/src/domains/therapist/components/TherapistCardEventType'

import useFeedback from '../../hooks/useFeedback'
import useIsAppWebView from '../../hooks/useIsAppWebView'
import useTherapist from '../../hooks/useTherapist'
import useUserHasCompletedSession from '../../hooks/useUserHasCompletedSession'
import { getFullName } from '../../utilities/name'
import Icon from '../icons/Icon'
import IconTextRebrand from '../icons/IconTextRebrand'
import Modal from '../modal/Modal'
import Recommendations from './Recommendations'
import TherapistProfileImage from './TherapistProfileImage'
import VerifiedTraining from './VerifiedTraining'

type Props = {
  price?: number
  therapistId: number
  isLinkToProfile?: boolean
  lastColumnElement?: React.ReactNode
}

const TherapistProfileHeader: React.FC<Props> = ({
  price,
  therapistId,
  isLinkToProfile = false,
  lastColumnElement,
}) => {
  const { therapist } = useTherapist(therapistId)
  const { t } = getTranslate()
  const { feedback, isLoading: feedbackLoading } = useFeedback(therapistId)
  const { isTherapist } = useUser()
  const userHasCompletedSession = useUserHasCompletedSession(therapistId)
  const isWebView = useIsAppWebView()
  const displayLanguages = useFeatureIsOn('display-therapist-languages')
  const { shouldHidePriceForService } = useCompanyBenefits()
  const { selectedServiceId } = useServices()
  const { categories } = useTherapistCategories()
  const shouldUseTherapistCategories = useFeatureIsOn('therapist-categories')

  const [videoVisible, setVideoVisible] = useState(false)

  const {
    experience,
    recommendations,
    image,
    firstName,
    lastName,
    titleAfter,
    titleBefore,
    videoId,
    verifiedCertificate,
  } = therapist || {}
  const hasFeedback = Boolean(feedback?.id) && !feedbackLoading
  const showFeedbackButton = !isTherapist && !feedbackLoading && !hasFeedback && userHasCompletedSession
  const fullName = getFullName({ person: { firstName, lastName }, titleAfter, titleBefore })

  if (!therapist) {
    return null
  }

  const displayRecommendations = typeof recommendations === 'number' && recommendations > 0
  const displayEditReview = !isWebView && hasFeedback
  const displayReviewPrompt = !isWebView && showFeedbackButton
  const displayCategories = Boolean(shouldUseTherapistCategories && categories && categories.length > 0)
  const displayVerifiedTraining = !displayCategories && typeof verifiedCertificate === 'number'

  const category = categories?.find((category) => category.id === therapist.therapistCategoryId)

  const renderEditReview = () => (
    <IconTextRebrand
      className="items-center"
      iconName="check-inline"
      text={
        <div className="flex w-full flex-wrap">
          <div className="mr-2">
            <Markdown>{t('component.therapistProfileView.feedbackSendText')}</Markdown>
          </div>
          <ButtonLink
            href={`/feedback/${therapistId}`}
            variant="inline"
            className="text-sm normal-case underline hover:no-underline"
          >
            {t('component.therapistProfileView.editFeedback')}
            <Icon icon="arrow" color="primary" size="xs" className="ml-1" />
          </ButtonLink>
        </div>
      }
    />
  )

  return (
    <>
      <Box>
        <div className="-m-3 flex flex-wrap items-center">
          <Col className="tablet:w-7/12 laptop:w-5/12">
            <div className="-m-3 flex items-center">
              <TherapistProfileImage
                identifier={fullName}
                href={isLinkToProfile ? `/client/${therapistId}/profile` : undefined}
                openModal={videoId ? () => setVideoVisible(true) : null}
                source={image}
                height={110}
                width={100}
                className="mr-1"
              />
              <div className="p-3">
                {Number.isInteger(experience) ? (
                  <Tag variant="yellow" size="xs">
                    {t('component.therapistProfileView.experience', {
                      experience,
                    })}
                  </Tag>
                ) : null}
                {isLinkToProfile ? (
                  <Link href={`/client/${therapistId}/profile`} className="text-secondary hover:text-secondary">
                    <h3 className="mb-0 text-xl leading-6">{fullName}</h3>
                  </Link>
                ) : (
                  <h3 className="mb-0 text-xl leading-6">{fullName}</h3>
                )}
                {typeof price === 'number' && price > 0 && !shouldHidePriceForService(selectedServiceId) && (
                  <div className="mt-2 text-sm">{t('component.therapistProfileView.price', { price })}</div>
                )}
              </div>
            </div>
            <Modal closeModal={() => setVideoVisible(false)} size="lg" video visible={videoVisible}>
              <YoutubeVideo videoId={videoId} />
            </Modal>
          </Col>
          <Col className="flex flex-col gap-3 tablet:w-5/12 laptop:w-4/12">
            {displayVerifiedTraining ? (
              <VerifiedTraining verified={verifiedCertificate} />
            ) : displayCategories && category ? (
              <TherapistCategory category={category} />
            ) : null}
            {displayRecommendations && <Recommendations recommendations={recommendations} />}
            <TherapistCardEventType therapist={therapist} />
            {displayLanguages && <SpeaksLanguages languages={therapist.languages} />}
            {(lastColumnElement || displayEditReview) && (
              <div className="laptop:hidden">{lastColumnElement || renderEditReview()}</div>
            )}
          </Col>
          {(lastColumnElement || displayEditReview) && (
            <Col className="-mt-3 hidden tablet:justify-center laptop:mt-0 laptop:flex laptop:w-3/12">
              {lastColumnElement || renderEditReview()}
            </Col>
          )}
        </div>
      </Box>
      {displayReviewPrompt && (
        <Box className="-mt-4 flex flex-wrap items-center justify-center py-2">
          <div className="mr-6 text-base font-semibold">
            <Markdown>{t('component.therapistProfileView.feedbackText')}</Markdown>
          </div>
          <ButtonLink href={`/feedback/${therapistId}`} variant="light" size="small">
            {t('component.button.giveFeedback')}
            <Icon icon="arrow" color="primary" size="xs" className="ml-3" />
          </ButtonLink>
        </Box>
      )}
    </>
  )
}

const Col = ({ children, className = '' }) => <div className={cn('w-full p-3', className)}>{children}</div>

const Box = ({ children, className = '' }) => (
  <div className={cn('mb-5 rounded-xl bg-white p-6', className)}>{children}</div>
)

export default memo<Props>(TherapistProfileHeader)
